/**
 * Paths for documents a browser opened.
 *
 * The copied renderer keys everything on path strings: tabs, recent files, the
 * last session, journals, versions, wiki links. A browser never reveals where a
 * file is on disk, so each document gets a path of the same shape that names
 * how it was reached instead, and the platform layer maps it back to a handle.
 *
 *   ws:<id>/<Folder>/notes/a.md    a file inside a folder opened with read/write
 *                                  access; <id> names the folder's handle
 *   snap:<id>/<Folder>/notes/a.md  a file inside a folder read as a snapshot
 *                                  (no File System Access): readable, not writable
 *   fh:<id>/<name>                 a single file with a handle of its own
 *   copy:<id>/<name>               a single file read without a handle; saving
 *                                  downloads a copy
 *   download:<id>/<name>           a document whose only save target is the
 *                                  browser's downloads
 *
 * Every separator is `/`, and the last segment is always the file's real name,
 * so the renderer's basename and directory helpers work on them unchanged.
 */

export type Scheme = 'ws' | 'snap' | 'fh' | 'copy' | 'download'

export interface ParsedPath {
  scheme: Scheme
  id: string
  /**
   * After the id. For `ws:` and `snap:` the first is the folder's own name and
   * the rest lead to the file; for the others it is just the file's name.
   */
  segments: string[]
}

const PATTERN = /^(ws|snap|fh|copy|download):([0-9a-f]+)(?:\/(.*))?$/

export function parsePath(path: string): ParsedPath | null {
  const m = PATTERN.exec(path)
  if (!m) return null
  const segments = (m[3] ?? '').split('/').filter((s) => s.length > 0)
  return { scheme: m[1] as Scheme, id: m[2], segments }
}

export function formatPath(scheme: Scheme, id: string, segments: string[]): string {
  return segments.length ? `${scheme}:${id}/${segments.join('/')}` : `${scheme}:${id}`
}

/** True for a folder scheme, whose first segment is the folder itself. */
export function isFolderScheme(scheme: Scheme): boolean {
  return scheme === 'ws' || scheme === 'snap'
}

/**
 * The segments inside the opened folder, below its root: `notes/a.md` for
 * `ws:<id>/Folder/notes/a.md`. Null for a path that is not in a folder, or
 * that climbs out of it.
 */
export function insideFolder(p: ParsedPath): string[] | null {
  if (!isFolderScheme(p.scheme) || p.segments.length === 0) return null
  const out: string[] = []
  for (const s of p.segments.slice(1)) {
    if (s === '.' || s === '') continue
    if (s === '..') {
      if (out.length === 0) return null
      out.pop()
    } else out.push(s)
  }
  return out
}

/** The root of a folder path: `ws:<id>/<Folder>`. */
export function folderRoot(p: ParsedPath): string | null {
  if (!isFolderScheme(p.scheme) || p.segments.length === 0) return null
  return formatPath(p.scheme, p.id, [p.segments[0]])
}

/**
 * The path as a person should read it: the folder and the file inside it, or
 * the file's name. Never the id, which means nothing to anyone.
 */
export function displayPath(path: string): string {
  const p = parsePath(path)
  if (!p) return path
  if (isFolderScheme(p.scheme)) return p.segments.join('/')
  return p.segments[p.segments.length - 1] ?? path
}

export function baseName(path: string): string {
  return path.split('/').pop() ?? path
}

/**
 * Why a file or folder name cannot be used, or null when it can. The rules
 * are the strictest of the systems a folder might live on, Windows', so a
 * folder made in one browser opens everywhere.
 */
const RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i
// eslint-disable-next-line no-control-regex
const FORBIDDEN = /[<>:"/\\|?*\u0000-\u001f]/

export function validName(name: string): string | null {
  if (typeof name !== 'string' || name.trim().length === 0) return 'A name is needed.'
  if (name === '.' || name === '..') return 'That name is taken by the folder itself.'
  if (FORBIDDEN.test(name)) return 'A name cannot contain < > : " / \\ | ? or *.'
  if (/[. ]$/.test(name)) return 'A name cannot end with a dot or a space.'
  if (RESERVED.test(name)) return `The name ${name.split('.')[0]} is reserved on Windows.`
  if (name.length > 255) return 'That name is too long.'
  return null
}
